// WS-75 life: WebGPU allows 8 vertex buffers per draw, and three gives each attribute a buffer of its own. A model with many custom
// vertex attributes (the animals' coat palettes, marks; the people's colour slots) packs them into ONE interleaved float
// buffer; the shaders read them by name as before (attribute('_marks') ...).
import * as THREE from 'three/webgpu';

const packed = new WeakSet();
/**
 * Interleave `names` of geometry (those it has) into one Float32 buffer; quantised (normalised integer) data is stored as
 * its float value, so the shaders read the same numbers (meshopt-decoded attributes often come as one padded interleaved
 * buffer each). Idempotent (an attribute this packed already is left alone).
 */
export function interleave(geometry, names) {
  const list = names.map((n) => [n, geometry.attributes[n]]).filter(([, a]) => a && !(a.isInterleavedBufferAttribute && packed.has(a.data)));
  if (list.length < 2) return geometry;
  const count = list[0][1].count, stride = list.reduce((s, [, a]) => s + a.itemSize, 0);
  if (list.some(([, a]) => a.count !== count)) return geometry;
  const data = new Float32Array(count * stride), ib = new THREE.InterleavedBuffer(data, stride);
  packed.add(ib);
  let off = 0;
  for (const [n, a] of list) {
    for (let i = 0; i < count; i++) for (let c = 0; c < a.itemSize; c++) data[i * stride + off + c] = a.getComponent(i, c);
    geometry.setAttribute(n, new THREE.InterleavedBufferAttribute(ib, a.itemSize, off, false));
    off += a.itemSize;
  }
  return geometry;
}

/** names of a geometry's attributes other than the ones three.js binds itself (position, normal, uv, skinning, tangent) */
export const customAttributes = (geometry, keep = ['position', 'normal', 'uv', 'skinIndex', 'skinWeight', 'tangent']) =>
  Object.keys(geometry.attributes).filter((n) => !keep.includes(n));

// ---- VIS-267 lossless (begin): welding: one vertex per distinct set of values (every 32-bit word of every attribute equal: -0 and 0 stay apart, so
// does every NaN pattern). The triangles stay the same, in the same order, with the same values at their corners: the
// vertex stage computes the same thing for each corner, so every pass draws the same picture (nothing reads a vertex's
// number). Parts are merged with their seams, flat faces and copies listed again and again (Aurora's static shadow
// proxy: 0.54 M vertices for 0.17 M places).
const words = (a) => new Uint32Array(a.buffer, a.byteOffset, a.byteLength >> 2);
/** the first vertex with the same words as each vertex: { remap: vertex -> new number, reps: new number -> its first vertex, m } */
function distinct(W, sizes, n) {
  let cap = 1024; while (cap < n * 2) cap *= 2;
  const table = new Int32Array(cap).fill(-1), remap = new Uint32Array(n), reps = new Uint32Array(n), M = cap - 1, P = W[0], s0 = sizes[0];
  const same = (i, j) => { for (let k = 0; k < W.length; k++) { const w = W[k], s = sizes[k]; for (let c = 0; c < s; c++) if (w[i * s + c] !== w[j * s + c]) return false; } return true; };
  let m = 0;
  for (let i = 0; i < n; i++) {
    let h = 0;
    for (let c = 0; c < s0; c++) h = Math.imul(h ^ P[i * s0 + c], 0x9e3779b1); // (the first attribute's words: positions)
    h = (h ^ (h >>> 15)) & M;
    for (;;) {
      const j = table[h];
      if (j < 0) { table[h] = m; reps[m] = i; remap[i] = m++; break; }
      if (same(i, reps[j])) { remap[i] = j; break; }
      h = (h + 1) & M;
    }
  }
  return { remap, reps, m };
}
/**
 * A positions-only triangle list or indexed mesh, welded. pos: Float32Array of xyz; index: the triangles' vertex
 * indices (null: 0..n-1). -> { pos, index: Uint32Array }
 */
export function weldPositions(pos, index = null) {
  const n = pos.length / 3, src = words(pos), { remap, reps, m } = distinct([src], [3], n), out = new Float32Array(m * 3), dst = words(out);
  for (let j = 0; j < m; j++) { const i = reps[j]; dst[j * 3] = src[i * 3]; dst[j * 3 + 1] = src[i * 3 + 1]; dst[j * 3 + 2] = src[i * 3 + 2]; } // (copied as words: bit for bit)
  const idx = new Uint32Array(index ? index.length : n);
  if (index) for (let k = 0; k < index.length; k++) idx[k] = remap[index[k]]; else idx.set(remap);
  return { pos: out, index: idx };
}
/**
 * An indexed geometry welded in place (its index 16-bit when every vertex fits, as the WebGL2 backend draws it; groups,
 * draw range and bounds stay as they are: the same corners). Only plain attributes of 32-bit values (float32, int32,
 * uint32), no morphs; anything else is left as it was. -> the geometry
 */
export function weldGeometry(g) {
  const I = g.index, names = Object.keys(g.attributes), A = names.map((k) => g.attributes[k]);
  if (!I || !A.length || Object.keys(g.morphAttributes || {}).length || A.some((a) => a.isInterleavedBufferAttribute || a.array.BYTES_PER_ELEMENT !== 4 || a.count !== A[0].count)) return g;
  const n = A[0].count, { remap, reps, m } = distinct(A.map((a) => words(a.array)), A.map((a) => a.itemSize), n);
  if (m === n) return g;
  A.forEach((a, k) => {
    const s = a.itemSize, dst = new a.array.constructor(m * s), sw = words(a.array), dw = words(dst);
    for (let j = 0; j < m; j++) for (let c = 0, i = reps[j]; c < s; c++) dw[j * s + c] = sw[i * s + c];
    const b = new THREE.BufferAttribute(dst, s, a.normalized);
    b.name = a.name; b.usage = a.usage; b.gpuType = a.gpuType;
    g.setAttribute(names[k], b);
  });
  const ix = I.array, out = new (m <= 65535 ? Uint16Array : ix.constructor)(ix.length); // (65535 itself is a strip's restart value: three's WebGPU backend rewrites it)
  for (let k = 0; k < ix.length; k++) out[k] = remap[ix[k]];
  const ni = new THREE.BufferAttribute(out, 1);
  ni.usage = I.usage;
  g.setIndex(ni);
  return g;
}
// ---- VIS-267 lossless (end)
